// Trickjump lines in the world: a flat ribbon (8 units wide, like ETJump's
// draw4VertexLine) along each trail of a route, coloured by speed, and square
// markers where the jumps start (green) and where the route starts and ends
// (red). The last recorded route of a map is kept in localStorage.

import * as THREE from 'three';
import { speedColor, speedRange, parseRoute, type TjlRoute } from '../shared/etjump/tjl';

const LINE_WIDTH = 8;
const MARKER_SIZE = 10;
const STORE_PREFIX = 'tjgame.tjl:';

export const tjlStore = {
  load(map: string): TjlRoute | null {
    try {
      return parseRoute(localStorage.getItem(STORE_PREFIX + map));
    } catch {
      return null;
    }
  },
  save(map: string, route: TjlRoute | null): void {
    try {
      if (route) localStorage.setItem(STORE_PREFIX + map, JSON.stringify(route));
      else localStorage.removeItem(STORE_PREFIX + map);
    } catch (err) {
      console.warn('could not store the trickjump line', err);
    }
  },
};

export class TjLinesView {
  readonly object = new THREE.Group();
  private readonly material = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, transparent: true, opacity: 0.85, depthWrite: false });
  private geometries: THREE.BufferGeometry[] = [];

  constructor() {
    this.object.renderOrder = 3;
  }

  show(route: TjlRoute | null): void {
    this.clear();
    if (!route) return;
    const { min, max } = speedRange(route);
    const pos: number[] = [];
    const col: number[] = [];
    const quad = (a: number[], b: number[], c: number[], d: number[], ca: number[], cb: number[]) => {
      pos.push(...a, ...b, ...c, ...a, ...c, ...d);
      col.push(...ca, ...cb, ...cb, ...ca, ...cb, ...ca);
    };
    route.trails.forEach((trail, ti) => {
      for (let i = 0; i + 1 < trail.length; i++) {
        const s = trail[i].pos;
        const e = trail[i + 1].pos;
        // horizontal normal of the segment, half the width
        let nx = -(e[1] - s[1]);
        let ny = e[0] - s[0];
        const len = Math.hypot(nx, ny) || 1;
        nx = (nx / len) * (LINE_WIDTH / 2);
        ny = (ny / len) * (LINE_WIDTH / 2);
        const c1 = speedColor(trail[i].speed, min, max);
        const c2 = speedColor(trail[i + 1].speed, min, max);
        quad([s[0] + nx, s[1] + ny, s[2] + 1], [e[0] + nx, e[1] + ny, e[2] + 1], [e[0] - nx, e[1] - ny, e[2] + 1], [s[0] - nx, s[1] - ny, s[2] + 1], c1, c2);
      }
      // markers: the route's ends red, the jumps green (addJumpIndicator)
      const marker = (p: number[], color: number[]) => {
        const h = MARKER_SIZE;
        quad([p[0] - h, p[1] - h, p[2] + 1.5], [p[0] + h, p[1] - h, p[2] + 1.5], [p[0] + h, p[1] + h, p[2] + 1.5], [p[0] - h, p[1] + h, p[2] + 1.5], color, color);
      };
      if (!trail.length) return;
      const red = [1, 0, 0];
      const green = [0, 1, 0];
      const last = route.trails.length - 1;
      if (route.trails.length === 1) {
        marker(trail[0].pos, red);
        marker(trail[trail.length - 1].pos, red);
      } else if (ti === 0) marker(trail[0].pos, red);
      else if (ti === last) {
        marker(trail[0].pos, green);
        marker(trail[trail.length - 1].pos, red);
      } else marker(trail[0].pos, green);
    });
    if (!pos.length) return;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.computeBoundingSphere();
    this.geometries.push(geo);
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.renderOrder = 3;
    this.object.add(mesh);
  }

  clear(): void {
    this.object.clear();
    this.geometries.forEach((g) => g.dispose());
    this.geometries = [];
  }

  dispose(): void {
    this.clear();
    this.material.dispose();
  }
}
